import React, { useState } from 'react';
import { User, MapPin, Calendar, MessageSquare, Star, Edit, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const ProfilePage = () => {
  const { user, logout } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    location: user?.location || '',
    children_ages: user?.children_ages || []
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const addChildAge = () => {
    setFormData({
      ...formData,
      children_ages: [...formData.children_ages, 0]
    });
  };

  const updateChildAge = (index, age) => {
    const newAges = [...formData.children_ages];
    newAges[index] = parseInt(age) || 0;
    setFormData({
      ...formData,
      children_ages: newAges
    });
  };

  const removeChildAge = (index) => {
    setFormData({
      ...formData,
      children_ages: formData.children_ages.filter((_, i) => i !== index)
    });
  };

  const handleSave = () => {
    // TODO: Implement save functionality
    setIsEditing(false);
  };

  const contributionActivities = [
    { type: 'venue', action: 'Added venue', name: 'Chisago Lakes Community Center', points: 10, date: '2 days ago' },
    { type: 'review', action: 'Reviewed', name: 'Wild River State Park', points: 3, date: '5 days ago' },
    { type: 'event', action: 'Added event', name: 'Sensory-Friendly Movie Night', points: 5, date: '1 week ago' },
    { type: 'discussion', action: 'Started discussion', name: 'Holiday Tips for Sensory Kids', points: 5, date: '2 weeks ago' }
  ];

  const achievements = [
    { name: 'Community Helper', description: 'Added your first venue', icon: '🏆', earned: true },
    { name: 'Helpful Reviewer', description: 'Left 5+ venue reviews', icon: '⭐', earned: true },
    { name: 'Event Organizer', description: 'Added 3+ events', icon: '🎯', earned: false },
    { name: 'Discussion Starter', description: 'Started 10+ discussions', icon: '💬', earned: false }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900" data-testid="profile-title">
            Your Profile
          </h1>
          <p className="text-gray-600 mt-2">
            Manage your account and track your community contributions
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Profile Info */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Account Information</h2>
                <button
                  onClick={() => isEditing ? handleSave() : setIsEditing(true)}
                  className="flex items-center px-3 py-2 text-blue-600 hover:text-blue-800"
                  data-testid="edit-profile-btn"
                >
                  <Edit size={16} className="mr-1" />
                  {isEditing ? 'Save' : 'Edit'}
                </button>
              </div>

              {isEditing ? (
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={user?.email || ''}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                  </div>

                  <div>
                    <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                      Location
                    </label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      placeholder="City, State"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Children's Ages
                    </label>
                    {formData.children_ages.map((age, index) => (
                      <div key={index} className="flex items-center space-x-2 mb-2">
                        <input
                          type="number"
                          min="0"
                          max="25"
                          value={age}
                          onChange={(e) => updateChildAge(index, e.target.value)}
                          className="w-20 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Age"
                        />
                        <button
                          type="button"
                          onClick={() => removeChildAge(index)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addChildAge}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      + Add Child Age
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center">
                    <User size={20} className="text-gray-400 mr-3" />
                    <div>
                      <p className="font-medium text-gray-900">{user?.name}</p>
                      <p className="text-sm text-gray-600">Member since {new Date(user?.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <span className="text-gray-400 mr-3">📧</span>
                    <p className="text-gray-700">{user?.email}</p>
                  </div>

                  {user?.location && (
                    <div className="flex items-center">
                      <MapPin size={20} className="text-gray-400 mr-3" />
                      <p className="text-gray-700">{user.location}</p>
                    </div>
                  )}

                  {user?.children_ages && user.children_ages.length > 0 && (
                    <div className="flex items-center">
                      <span className="text-gray-400 mr-3">👶</span>
                      <div>
                        <p className="text-gray-700">
                          Children: {user.children_ages.join(', ')} years old
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Logout Button */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={logout}
                  className="flex items-center text-red-600 hover:text-red-800"
                  data-testid="logout-btn"
                >
                  <LogOut size={16} className="mr-2" />
                  Sign Out
                </button>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
              <div className="space-y-4">
                {contributionActivities.map((activity, index) => (
                  <div key={index} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                    <div className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                        activity.type === 'venue' ? 'bg-blue-100 text-blue-600' :
                        activity.type === 'review' ? 'bg-yellow-100 text-yellow-600' :
                        activity.type === 'event' ? 'bg-green-100 text-green-600' :
                        'bg-purple-100 text-purple-600'
                      }`}>
                        {activity.type === 'venue' ? <MapPin size={16} /> :
                         activity.type === 'review' ? <Star size={16} /> :
                         activity.type === 'event' ? <Calendar size={16} /> :
                         <MessageSquare size={16} />}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{activity.action}</p>
                        <p className="text-sm text-gray-600">{activity.name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-green-600">+{activity.points} points</p>
                      <p className="text-xs text-gray-500">{activity.date}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contribution Score */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Contribution Score</h3>
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2" data-testid="contribution-score">
                  {user?.contribution_score || 0}
                </div>
                <p className="text-gray-600">Total Points</p>
              </div>
              
              <div className="mt-6 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Add venue:</span>
                  <span className="font-medium">+10 points</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Add event:</span>
                  <span className="font-medium">+5 points</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Write review:</span>
                  <span className="font-medium">+3 points</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Start discussion:</span>
                  <span className="font-medium">+5 points</span>
                </div>
              </div>
            </div>

            {/* Achievements */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Achievements</h3>
              <div className="space-y-3">
                {achievements.map((achievement, index) => (
                  <div
                    key={index}
                    className={`flex items-center p-3 rounded-lg ${
                      achievement.earned ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <span className="text-2xl mr-3">{achievement.icon}</span>
                    <div className="flex-1">
                      <p className={`font-medium ${achievement.earned ? 'text-green-800' : 'text-gray-600'}`}>
                        {achievement.name}
                      </p>
                      <p className={`text-sm ${achievement.earned ? 'text-green-600' : 'text-gray-500'}`}>
                        {achievement.description}
                      </p>
                    </div>
                    {achievement.earned && (
                      <span className="text-green-600 font-bold text-sm">✓</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;